// client/src/components/plataforma/contas/ContaEquipePage.js - VERSÃO FINAL 100% CORRIGIDA
import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    useAccountById, 
    useSafeAccountUsers,
    useMutateAccountUsers 
} from '../../../hooks/useAccounts';
import { useRelationshipsMutations } from '../../../hooks/useRelationships';
import {
    Users, Plus, Loader2, RefreshCw,
    Filter as FilterIcon, XCircle, Search,
    Eye, Key, UserX, UserCheck, Trash2
} from 'lucide-react';
import EquipePanel from '../EquipePanel';
import useAlert from '../../../hooks/useAlert';
import PermissionGuard from '../../routing/PermissionGuard';
import Pagination from '../../common/Pagination';
import ActionsDrawer from '../../common/ActionsDrawer';
import PageHeader from '../../layout/PageHeader';
import FabButton from '../../common/FabButton';

const ContaEquipePage = () => {
    const { id: contaId } = useParams();
    const navigate = useNavigate();
    const { setAlert } = useAlert();

    const [currentPage, setCurrentPage] = useState(1);
    const [showFilters, setShowFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState({ search: '', status: '', role: '' });
    const [searchInput, setSearchInput] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const usersPerPage = 50;

    const { data: accountDetails, isLoading: accountLoading } = useAccountById(contaId, { enabled: !!contaId });
    const { data: accountUsersResponse, isLoading: usersLoading, refetch } = useSafeAccountUsers(contaId, { enabled: !!contaId });
    
    // Mutations para Ações
    const { resetAccountUserPassword, toggleUserActiveStatus } = useMutateAccountUsers();
    const { deleteAccountUser, invalidateRelationships } = useRelationshipsMutations();

    const uniqueRoles = useMemo(() => {
        const roles = new Set();
        const rawData = accountUsersResponse?.data || (Array.isArray(accountUsersResponse) ? accountUsersResponse : []);
        rawData.forEach(item => { if (item.role) roles.add(typeof item.role === 'object' ? item.role.name : item.role); });
        return Array.from(roles).filter(Boolean);
    }, [accountUsersResponse]);

    const processedUsers = useMemo(() => {
        const rawData = accountUsersResponse?.data || (Array.isArray(accountUsersResponse) ? accountUsersResponse : []);
        if (rawData.length === 0) return [];
        
        const users = rawData.map(item => {
            const userObj = item.user || item;
            const roleName = item.role?.name || item.role || '';
            const isActive = item.status === 'ativo' && (userObj.status !== 'inativo');

            return {
                ...userObj,
                _id: userObj._id || userObj.id,
                relationshipId: item._id,
                roleName,
                isActive,
                telefone: userObj.telefone || '-',
                photoUrl: userObj.photoUrl || userObj.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(userObj.name)}&background=random`
            };
        });

        if (activeFilters.search) {
            const term = activeFilters.search.toLowerCase();
            return users.filter(u => u.name?.toLowerCase().includes(term) || u.email?.toLowerCase().includes(term));
        }
        return users;
    }, [accountUsersResponse, activeFilters.search]);

    const totalUsers = processedUsers.length;
    const paginatedUsers = processedUsers.slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage);

    const handleEdit = useCallback((u) => {
        navigate(`/contas/${contaId}/equipe/${u._id}/editar`, { state: { contaId, userId: u._id } });
        setSelectedUser(null);
    }, [navigate, contaId]);

    // ✅ CORREÇÃO: Lógica de Reset de Senha com exibição do código
    const handleResetPassword = useCallback((userId, userName) => {
        if (window.confirm(`Deseja resetar a senha de ${userName}? O sistema gerará um código temporário.`)) {
            resetAccountUserPassword.mutate({ userId }, {
                onSuccess: (response) => {
                    const tempPass = response.temporaryPassword || response.data?.temporaryPassword;
                    setAlert('Senha resetada com sucesso!', 'success');
                    if (tempPass) {
                        setTimeout(() => {
                            setAlert(`CÓDIGO DE ACESSO PARA ${userName.toUpperCase()}: ${tempPass}`, 'info', 25000);
                        }, 500);
                    }
                },
                onError: (err) => setAlert(err.response?.data?.error || 'Erro ao resetar senha', 'danger')
            });
        }
        setSelectedUser(null);
    }, [resetAccountUserPassword, setAlert]);

    const handleToggleStatus = useCallback((user) => {
        if (window.confirm(`Deseja ${user.isActive ? 'desativar' : 'ativar'} o acesso de ${user.name}?`)) {
            toggleUserActiveStatus.mutate({ userId: user.relationshipId, isActive: !user.isActive }, {
                onSuccess: () => {
                    setAlert('Status atualizado com sucesso!', 'success');
                    refetch();
                },
                onError: (err) => setAlert('Erro ao atualizar status', 'danger')
            });
        }
        setSelectedUser(null);
    }, [toggleUserActiveStatus, setAlert, refetch]);

    const handleDeleteUser = useCallback((user) => {
        if (window.confirm(`Tem certeza que deseja remover ${user.name} desta conta?`)) {
            deleteAccountUser.mutate(user.relationshipId, {
                onSuccess: () => {
                    setAlert('Usuário removido da conta!', 'success');
                    invalidateRelationships();
                    refetch();
                },
                onError: (err) => setAlert('Erro ao remover usuário', 'danger')
            });
        }
        setSelectedUser(null);
    }, [deleteAccountUser, setAlert, invalidateRelationships, refetch]);

    return (
        <PermissionGuard requiredPermission="equipe:view">
            <div className="page-management-container">
                {/* Antes montava o cabecalho na mao — os icones dos 3 botoes
                    tambem nao tinham className="icon" (o gap so vem dessa
                    classe), entao ficavam colados no texto. O botao
                    "Filtros" tambem nao fazia nada — showFilters nunca
                    controlava a renderizacao de painel nenhum (diferente da
                    pagina irma FranquiaUsersPage.js, que tem o mesmo botao
                    de verdade). Convertido pra PageHeader e completado o
                    painel de busca que faltava. */}
                <PageHeader
                    icon={<Users />}
                    title="Equipe da Conta"
                    subtitle={`Gestão de ${accountDetails?.name || 'Carregando...'}`}
                    navBack={{ to: -1, text: 'Voltar' }}
                    actions={<>
                        <button onClick={() => setShowFilters(!showFilters)} className="action-button action-button-secondary">
                            <FilterIcon size={18} className="icon"/> Filtros
                        </button>
                        <button onClick={() => refetch()} className="action-icon-button" title="Atualizar">
                            <RefreshCw size={18} className={usersLoading ? 'animate-spin' : ''}/>
                        </button>
                    </>}
                />

                {showFilters && (
                    <div className="content-panel mb-4 p-4">
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => {
                                    setSearchInput(e.target.value);
                                    setActiveFilters(prev => ({ ...prev, search: e.target.value }));
                                }}
                                placeholder="Buscar por nome ou e-mail..."
                                className="form-input pl-10"
                            />
                        </div>
                    </div>
                )}

                <EquipePanel
                    users={paginatedUsers}
                    isLoading={usersLoading}
                    onOpenActions={setSelectedUser}
                />
            </div>

            <ActionsDrawer
                open={!!selectedUser}
                onClose={() => setSelectedUser(null)}
                title={selectedUser?.name || 'Usuário'}
                subtitle={selectedUser?.roleName}
                actions={selectedUser ? [
                    {
                        key: 'editar',
                        label: 'Visualizar / Editar',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => handleEdit(selectedUser),
                    },
                    ...(selectedUser.status !== 'excluido' ? [{
                        key: 'resetar-senha',
                        label: 'Resetar Senha',
                        icon: <Key size={20} className="icon" />,
                        onClick: () => handleResetPassword(selectedUser._id || selectedUser.id, selectedUser.name),
                    }] : []),
                    ...(selectedUser.status !== 'excluido' ? [{
                        key: 'toggle-status',
                        label: selectedUser.isActive ? 'Desativar Acesso' : 'Reativar Acesso',
                        icon: selectedUser.isActive ? <UserX size={20} className="icon" /> : <UserCheck size={20} className="icon" />,
                        onClick: () => handleToggleStatus(selectedUser),
                    }] : []),
                    ...(selectedUser.status !== 'excluido' ? [{
                        key: 'excluir',
                        label: 'Remover da Conta',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDeleteUser(selectedUser),
                        destructive: true,
                    }] : []),
                ] : []}
            />

            <FabButton onClick={() => navigate(`/contas/${contaId}/equipe/novo`, { state: { contaId, isCreatingMode: true } })} title="Novo Usuário" icon={<Users size={26} className="icon" />} />
        </PermissionGuard>
    );
};

export default ContaEquipePage;